import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropertyMap from "../components/PropertyMap.jsx";
import { listProperties } from "../admin/properties.js";
import { fetchImagesByPath } from "./photos.jsx";
import Slide from "./slide.jsx";
import "../slideshow.css";

function getPropertyRoute(propertyId) {
  if (propertyId === "Summer") {
    return "/Summer";
  }

  if (propertyId === "Main") {
    return "/Main";
  }

  return `/properties/${encodeURIComponent(propertyId)}`;
}

function compareProperties(firstProperty, secondProperty) {
  const propertyOrder = {
    Summer: 0,
    Main: 1,
  };
  const firstRank = propertyOrder[firstProperty.id] ?? 100;
  const secondRank = propertyOrder[secondProperty.id] ?? 100;

  if (firstRank === secondRank) {
    return firstProperty.label.localeCompare(secondProperty.label);
  }

  return firstRank - secondRank;
}

export function sortPropertyShowcases(properties = []) {
  return [...properties].sort(compareProperties);
}

function getAvailableUnitsLabel(property) {
  const availableUnits = property.units.filter(
    (unit) => unit.available || unit.availabilityStatusClass === "scheduled"
  ).length;
  const unitLabel = availableUnits === 1 ? "unit" : "units";

  return `${availableUnits} ${unitLabel} available or upcoming`;
}

function getPropertyLocationLabel(property) {
  const rawLocation = String(property.address || property.mapQuery || "").trim();

  if (!rawLocation) {
    return "Nova Scotia";
  }

  return rawLocation.replace(/,\s*Canada$/i, "").trim();
}

function getPropertyPreviewDescription(property) {
  if (property.description?.trim()) {
    return property.description.trim();
  }

  if (property.id === "Summer") {
    return "Spacious student-friendly suites with a mix of shared and private living options near Wolfville.";
  }

  if (property.id === "Main") {
    return "Comfortable apartment living with practical layouts and easy access to the Wolfville area.";
  }

  return "Well-maintained rental housing managed by Pentagon Properties in Nova Scotia.";
}

export async function loadPropertyShowcases() {
  const propertyList = await listProperties();
  const propertySlides = await Promise.all(
    propertyList.map(async (property) => {
      try {
        const images = await fetchImagesByPath(property.documentPath);

        return {
          ...property,
          images,
        };
      } catch (error) {
        return {
          ...property,
          images: [],
        };
      }
    })
  );

  return sortPropertyShowcases(propertySlides);
}

function PropertyShowcaseList({ loading: providedLoading, properties: providedProperties } = {}) {
  const isControlled = Array.isArray(providedProperties);
  const [internalLoading, setInternalLoading] = useState(!isControlled);
  const [internalProperties, setInternalProperties] = useState([]);

  useEffect(() => {
    if (isControlled) {
      return undefined;
    }

    let isMounted = true;

    async function loadProperties() {
      try {
        const propertySlides = await loadPropertyShowcases();

        if (isMounted) {
          setInternalProperties(propertySlides);
        }
      } finally {
        if (isMounted) {
          setInternalLoading(false);
        }
      }
    }

    loadProperties();

    return () => {
      isMounted = false;
    };
  }, [isControlled]);

  const loading = isControlled ? Boolean(providedLoading) : internalLoading;
  const properties = isControlled ? providedProperties : internalProperties;

  if (loading && !properties.length) {
    return (
      <section className="property-showcase bg-light" id="Rentals">
        <div className="container property-showcase__container">
          <div className="text-center property-showcase__header">
            <h1 className="fw-bolder">Available Properties</h1>
            <p className="lead fw-normal text-muted mb-5">Loading property showcases...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!properties.length) {
    return (
      <section className="property-showcase bg-light" id="Rentals">
        <div className="container property-showcase__container">
          <div className="text-center property-showcase__header">
            <h1 className="fw-bolder">Available Properties</h1>
            <p className="lead fw-normal text-muted mb-5">No properties are published yet.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      {properties.map((property, index) => (
        <section
          className={`property-showcase bg-light ${
            index < properties.length - 1 ? "property-showcase--with-next" : ""
          } ${index > 0 ? "property-showcase--stacked" : ""}`.trim()}
          id={index === 0 ? "Rentals" : undefined}
          key={property.id}
        >
          <div className="container property-showcase__container">
            {index === 0 ? (
              <div className="text-center property-showcase__header">
                <h1 className="fw-bolder">Available Properties</h1>
                <p className="lead fw-normal text-muted mb-5">
                  Serving the Halifax and Wolfville area
                </p>
              </div>
            ) : null}

            <div className="property-showcase__card">
              <article className="property-showcase__info-card">
                <h2 className="property-showcase__title">{property.label}</h2>
                <p className="property-showcase__location">{getPropertyLocationLabel(property)}</p>
              </article>
              <div className="row align-items-start property-showcase__row">
                <div className="col-lg-8">
                  <Slide
                    alwaysShowControls
                    images={property.images}
                    imageFit="contain"
                    navPosition="bottom"
                    showHeader={false}
                    title={property.label}
                  />
                </div>
                <div className="col-lg-4 d-flex flex-column justify-content-center property-showcase__actions">
                  <div className="property-showcase__details-card">
                    <a className="btn btn-primary btn-lg mb-3 ms-1" href="#contact">
                      Inquire
                    </a>
                    <Link className="btn btn-primary btn-lg mb-3 ms-1" to={getPropertyRoute(property.id)}>
                      See Available Units
                    </Link>
                    <p className="property-showcase__availability-count">
                      {getAvailableUnitsLabel(property)}
                    </p>
                    <p className="property-showcase__description">
                      {getPropertyPreviewDescription(property)}
                    </p>
                    <PropertyMap mapQuery={property.mapQuery} title={property.label} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </>
  );
}

export default PropertyShowcaseList;
